// Dynamic-field placeholders the designer can drop into a text layer.
// Keys mirror exactly what the backend buildContext() resolves
// (services/certificate/context.js). Common fields apply to every type;
// per-type fields are appended for the template's certificate type.

export interface FieldDef {
  key: string
  label: string
}

export const COMMON_FIELDS: FieldDef[] = [
  { key: "student.full_name", label: "Student — Full Name" },
  { key: "student.fathers_name", label: "Student — Father's Name" },
  { key: "student.mothers_name", label: "Student — Mother's Name" },
  { key: "student.gr_number", label: "Student — GR Number" },
  { key: "student.class_name", label: "Student — Class" },
  { key: "student.section_name", label: "Student — Section" },
  { key: "student.roll_number", label: "Student — Roll Number" },
  { key: "student.son_or_daughter", label: "Student — son/daughter" },
  { key: "student.s_o_or_d_o", label: "Student — S/O or D/O" },
  { key: "student.he_or_she", label: "Student — he/she" },
  { key: "student.his_or_her", label: "Student — his/her" },
  { key: "student.him_or_her", label: "Student — him/her" },
  { key: "session.name", label: "Session — Name" },
  { key: "session.full", label: "Session — Full (2025-2026)" },
  { key: "session.short", label: "Session — Short (25-26)" },
  { key: "session.mixed", label: "Session — Mixed (2025-26)" },
  { key: "exam_term.name", label: "Exam Term — Name" },
  { key: "campus.name", label: "Campus — Name" },
  { key: "campus.code", label: "Campus — Code" },
  { key: "campus.address", label: "Campus — Address" },
  { key: "campus.phone", label: "Campus — Phone" },
  { key: "campus.logo", label: "Campus — Logo" },
  { key: "campus.logo_black", label: "Campus — Black Logo" },
  { key: "campus.logo_stroke", label: "Campus — Stroke Logo" },
  { key: "school.name", label: "School — Name" },
  { key: "school.code", label: "School — Code" },
  { key: "generated_at", label: "Generated Date" },
  { key: "batch.ref_number", label: "Batch Reference Number" },
  { key: "cert.ref_number", label: "Certificate Reference Number" },
]

export const TYPE_FIELDS: Record<string, FieldDef[]> = {
  class_result: [
    { key: "class_result.position", label: "Position (1ˢᵗ Position)" },
    { key: "class_result.position_number", label: "Position Number" },
    { key: "class_result.grade_letter", label: "Grade Letter" },
    { key: "class_result.percent", label: "Percentage" },
  ],
  sports: [
    { key: "sport.name", label: "Sport Name" },
    { key: "sport.position", label: "Sport Position" },
    { key: "sport.position_number", label: "Position Number" },
  ],
  participation: [
    { key: "participation.events", label: "Events (joined)" },
    { key: "participation.events_csv", label: "Events (comma list)" },
    { key: "participation.position", label: "Position" },
  ],
  winner: [
    { key: "winner.event_name", label: "Event Name" },
    { key: "winner.position", label: "Position" },
    { key: "sport.name", label: "Sport Name (if sport)" },
    { key: "competition.name", label: "Competition Name (if competition)" },
  ],
  competition: [
    { key: "competition.name", label: "Competition Name" },
    { key: "competition.position", label: "Position" },
  ],
  appreciation: [
    { key: "attribution.name", label: "Attribution Name" },
    { key: "attribution.message", label: "Message" },
  ],
  graduation: [{ key: "graduation.message", label: "Message" }],
  outstanding: [
    { key: "outstanding.message", label: "Message" },
    { key: "outstanding.event_name", label: "Event Name" },
  ],
  cooperative_parents: [{ key: "cooperative_parents.message", label: "Message" }],
  general: [
    { key: "general.full_name", label: "Recipient Name" },
    { key: "general.custom_text", label: "Custom Text" },
    { key: "general.message", label: "Message" },
    { key: "general.title", label: "Title" },
  ],
  best_teacher: [
    { key: "teacher.full_name", label: "Teacher Name" },
    { key: "teacher.title", label: "Teacher Title" },
    { key: "teacher.designation", label: "Teacher Designation" },
    { key: "teacher.subject", label: "Teacher Subject" },
    { key: "teacher.message", label: "Message" },
  ],
  attendance: [{ key: "attendance.ratio", label: "Attendance Ratio (present / total)" }],
  fee_sticker: [
    { key: "student.address", label: "Student — Address" },
    { key: "fee.monthly", label: "Fee — Monthly After Discount" },
    { key: "fee.tuition", label: "Fee — Tuition After Discount" },
    { key: "fee.st_fund", label: "Fee — ST-Fund After Discount" },
    { key: "fee.exam", label: "Fee — Exam After Discount" },
    { key: "fee.total", label: "Fee — Invoice Total After Discount" },
    { key: "fee.sub_total", label: "Fee — Invoice Subtotal" },
    { key: "fee.discount", label: "Fee — Total Discount" },
    { key: "fee.invoice_number", label: "Fee — Invoice Number" },
    { key: "fee.month", label: "Fee — Month" },
  ],
}

export const fieldsForType = (type: string): FieldDef[] => [
  ...COMMON_FIELDS,
  ...(TYPE_FIELDS[type] || []),
]
